const Q = {
  NAME: 'name',
  BREED: 'breed',
  AGE: 'age',
  AGE_KNOW: 'age-know',
  BIRTHDAY_YEAR: 'birthday-year',
  BIRTHDAY_MONTH: 'birthday-month',
  WEIGHT: 'weight',
  ISSUES: 'issues',
  FORMAT_PREFERENCE: 'format-preference',
  FOOD_ALLERGENS: 'food-allergens',
  OWNER_NAME: 'owner-name',
  CUSTOMER_EMAIL: 'customer-email',
};

const MULTIVITAMIN_FAMILY = 'mv';
const JOINT_CHEW_FAMILY = 'jc';
const JOINT_LIQUID_FAMILY = 'jl';

if (!customElements.get('quiz-issue-recommender')) {
  class QuizIssueRecommender extends HTMLElement {
    constructor() {
      super();
      this.boundOnQuizComplete = null;
      this.mainQuiz = null;
    }

    connectedCallback() {
      this.mainQuiz = this.closest('main-quiz');
      if (!this.mainQuiz) return;
      this.boundOnQuizComplete = this.onQuizComplete.bind(this);
      this.mainQuiz.addEventListener('quiz:complete', this.boundOnQuizComplete);
    }

    disconnectedCallback() {
      if (this.mainQuiz) {
        this.mainQuiz.removeEventListener('quiz:complete', this.boundOnQuizComplete);
      }
    }

    onQuizComplete(event) {
      const { quizData, quizUrl, recommender } = event.detail;
      const config = recommender.recommendationConfig || {};

      // Apply keyMap: rename raw quiz keys to their canonical URL param names
      const keyMap = {
        [Q.NAME]: 'name',
        [Q.AGE_KNOW]: 'birthday-known',
        [Q.AGE]: 'age',
        [Q.BREED]: 'breed',
        [Q.WEIGHT]: 'weight',
        [Q.ISSUES]: 'issues',
        [Q.FORMAT_PREFERENCE]: 'format-preference',
        [Q.FOOD_ALLERGENS]: 'dietary-restrictions',
        [Q.OWNER_NAME]: 'owner-name',
        [Q.CUSTOMER_EMAIL]: 'email',
      };

      // Rename any params already set on the URL that match keyMap keys
      Object.entries(keyMap).forEach(([rawKey, mappedKey]) => {
        const quizRawParam = 'quiz_' + rawKey;
        const quizMappedParam = 'quiz_' + mappedKey;
        if (quizUrl.searchParams.has(quizRawParam) && quizRawParam !== quizMappedParam) {
          const val = quizUrl.searchParams.get(quizRawParam);
          quizUrl.searchParams.delete(quizRawParam);
          quizUrl.searchParams.set(quizMappedParam, val);
        }
      });

      // Birthday encoding: combine birthday-year + birthday-month → quiz_birthday
      const birthdayYear = quizData[Q.BIRTHDAY_YEAR];
      const birthdayMonth = quizData[Q.BIRTHDAY_MONTH];
      if (birthdayYear || birthdayMonth) {
        const monthNum = {
          January: '01', February: '02', March: '03', April: '04',
          May: '05', June: '06', July: '07', August: '08',
          September: '09', October: '10', November: '11', December: '12',
        }[birthdayMonth] || (birthdayMonth || '').padStart(2, '0');
        quizUrl.searchParams.set('quiz_birthday', `${birthdayYear || ''}-${monthNum}`);

        // Compute age from birthday if available and set quiz_age
        if (typeof recommender.calcAgeFromBirthday === 'function') {
          const age = recommender.calcAgeFromBirthday(birthdayYear, birthdayMonth);
          if (age !== null) quizUrl.searchParams.set('quiz_age', age);
        }
      }

      // Remove raw birthday keys from URL — they were encoded above or not needed
      quizUrl.searchParams.delete('quiz_' + Q.BIRTHDAY_YEAR);
      quizUrl.searchParams.delete('quiz_' + Q.BIRTHDAY_MONTH);

      // Compute product recommendation and append quiz_recommended_categories
      const recommendation = QuizIssueRecommender.computeRecommendation(quizData, config);
      if (recommendation.urlParam) {
        quizUrl.searchParams.set('quiz_recommended_categories', recommendation.urlParam);
      }

      // Store result so buildTrackPayload can include klaviyo props
      recommender.recommendationResult = recommendation;
    }

    static computeRecommendation(quizData, config) {
      if (!config.issues) return { bundle: [], klaviyoProps: {}, urlParam: '' };

      const issueList = (quizData[Q.ISSUES] || '').split(',').map((v) => v.trim()).filter(Boolean);
      const nonJointFamilies = new Set();
      let jointTriggered = false;

      issueList.forEach((issue) => {
        const def = config.issues[issue];
        if (!def) return;
        if (def.family === 'joint') { jointTriggered = true; return; }
        nonJointFamilies.add(def.family);
      });

      // Resolve the joint family with allergen awareness so we never pick jc
      // when pork/fish-shellfish would remove it — fall back to jl in that case.
      let jointFamily = null;
      if (jointTriggered) {
        const formatVal = quizData[Q.FORMAT_PREFERENCE] || '';
        const formats = formatVal.split(',').map((v) => v.trim().toLowerCase()).filter(Boolean);
        const prefersLiquidOnly = formats.includes('liquids') && !formats.includes('chews');

        const allergenList = (quizData[Q.FOOD_ALLERGENS] || '').split(',').map((v) => v.trim()).filter(Boolean);
        const familiesToRemove = new Set();
        allergenList.forEach((allergen) => {
          const restriction = config.allergy_filter?.restrictions?.[allergen];
          if (restriction && !restriction.klaviyo_only) {
            restriction.removes_families.forEach((f) => familiesToRemove.add(f));
          }
        });

        const jcBlocked = familiesToRemove.has(JOINT_CHEW_FAMILY);
        const jlBlocked = familiesToRemove.has(JOINT_LIQUID_FAMILY);

        if (prefersLiquidOnly) {
          // User explicitly wants liquids; fall back to jc only if jl is blocked
          jointFamily = jlBlocked ? (jcBlocked ? null : JOINT_CHEW_FAMILY) : JOINT_LIQUID_FAMILY;
        } else {
          // User prefers chews (or no preference); fall back to jl if jc is blocked
          jointFamily = jcBlocked ? (jlBlocked ? null : JOINT_LIQUID_FAMILY) : JOINT_CHEW_FAMILY;
        }
      }

      // uniqueFamilies treats joint as a single slot so size-based bundle rules
      // count correctly (e.g. joint + ai = 2, not 3).
      const uniqueFamilies = new Set(nonJointFamilies);
      if (jointFamily) uniqueFamilies.add(jointFamily);

      if (uniqueFamilies.size === 0) return { bundle: [], klaviyoProps: {}, urlParam: '' };

      const hierarchy = config.bundle_rules?.priority_hierarchy || [];
      const singleBundles = config.bundle_rules?.single_product_bundles || {};
      let bundle = [];

      if (uniqueFamilies.size === 1) {
        const single = [...uniqueFamilies][0];
        bundle = [...(singleBundles[single]?.families || [single])];
      } else if (uniqueFamilies.size === 2) {
        bundle = [...uniqueFamilies];
        if (!bundle.includes(MULTIVITAMIN_FAMILY)) bundle.push(MULTIVITAMIN_FAMILY);
        bundle = [...new Set(bundle)];
      } else if (uniqueFamilies.size === 3) {
        bundle = [...uniqueFamilies];
      } else {
        bundle = [...uniqueFamilies]
          .sort((a, b) => hierarchy.indexOf(a) - hierarchy.indexOf(b))
          .slice(0, config.bundle_rules?.max_products || 3);
      }

      const allergenList = (quizData[Q.FOOD_ALLERGENS] || '').split(',').map((v) => v.trim()).filter(Boolean);
      const familiesToRemove = new Set();
      const klaviyoOnlyAllergens = [];

      allergenList.forEach((allergen) => {
        const restriction = config.allergy_filter?.restrictions?.[allergen];
        if (!restriction) return;
        if (restriction.klaviyo_only) {
          klaviyoOnlyAllergens.push(allergen);
        } else {
          restriction.removes_families.forEach((f) => familiesToRemove.add(f));
        }
      });

      bundle = bundle.filter((f) => !familiesToRemove.has(f));

      const labels = config.family_labels || {};
      const labelList = bundle.map((f) => labels[f]).filter(Boolean);

      const klaviyoProps = {
        dog1_recommended_product_category: labelList.join(', '),
        dog1_recommended_product_category_list: labelList,
      };

      return { bundle, klaviyoProps, urlParam: labelList.join(', ') };
    }
  }

  customElements.define('quiz-issue-recommender', QuizIssueRecommender);
}
